// ============================================
// Admin Route Guard
//
// Wraps admin routes. Checks Firebase Auth state:
// - Loading → spinner
// - Not authenticated → redirect to home
// - Authenticated but not in admin_users → redirect to home + access denied toast
// - Authenticated + admin_users → render children
// ============================================

import { type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { AdminAuthProvider, useAdminAuth } from '../../context/AdminAuthContext';

/** Session storage key used to signal access denied toast on redirect */
export const ACCESS_DENIED_KEY = 'hemologix_access_denied';

function AdminGuardInner({ children }: { children: ReactNode }) {
  const { status, signOut } = useAdminAuth();

  // Loading state
  if (status === 'loading') {
    return (
      <div className="admin-theme min-h-screen bg-[#FAFAFA] flex flex-col items-center justify-center gap-3 font-sans">
        <Loader2 size={32} className="text-[#E11D48] animate-spin" />
        <p className="text-[15px] text-[#52525B]">Verifying access...</p>
      </div>
    );
  }

  // Authenticated but not an admin — sign out and redirect with toast
  if (status === 'access_denied') {
    sessionStorage.setItem(ACCESS_DENIED_KEY, '1');
    signOut();
    return <Navigate to="/" replace />;
  }

  // Not authenticated → redirect to home
  if (status === 'unauthenticated') {
    return <Navigate to="/" replace />;
  }

  // Authenticated + admin role confirmed
  return <>{children}</>;
}

export function AdminGuard({ children }: { children: ReactNode }) {
  return (
    <AdminAuthProvider>
      <AdminGuardInner>{children}</AdminGuardInner>
    </AdminAuthProvider>
  );
}
